import './styles.scoped.css'
import {Typography, Button, IconButton, Modal, Avatar, Chip} from '@mui/material'
import {Close, DateRange, AccessTime} from '@mui/icons-material'
import {useMobile} from '../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../utils/helpers'
import {format} from 'date-fns'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'

export default ({open, setOpen, loadData, items = []}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()

    const plural = items?.length > 1
    const title = `Are you sure you want to delete ${plural ? 'these visits' : 'this visit'}?`
    const submitLabel = plural ? `Delete Visits (${items?.length})` : 'Delete Visit'

    const submit = () => {
        setLoading(true)
        scheduleApi
            .deleteScheduleVisits(branchId, items?.[0]?.schedule_id, {visit_ids: items?.map(item => item?.id)})
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    const [item] = items
                    setMessageBox({
                        status: 'success',
                        title: 'Visit Successfully Deleted',
                        description: `The visit for ${item?.schedule?.caregiver?.first_name + ' ' + item?.schedule?.caregiver?.last_name} on ${format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')} at ${formatTime(item?.start_time)} - ${formatTime(item?.end_time)} has been deleted and will no longer appear in the schedule list. The rest of the schedule is not affected.`,
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Visits Successfully Deleted',
                        description: `${items?.length} visits have been deleted and will no longer appear in the schedule list. The rest of the schedule is not affected.`,
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    {title}
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Back
                                </Button>
                                <Button variant="contained" color="error" fullWidth onClick={submit} style={{flex: 1}}>
                                    {submitLabel}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            {title}
                        </Typography>
                    )}
                    <br />
                    <Typography variant="body2" color="text.primary" className="title">
                        {plural
                            ? "Only these visits will be removed. They won't be recreated when the schedule is edited, and the rest of the schedule is not affected."
                            : "Only this visit will be removed. It won't be recreated when the schedule is edited, and the rest of the schedule is not affected."}
                    </Typography>
                    {items.map((item, index) => (
                        <div key={index}>
                            <br />
                            <div className="card">
                                <div className="card-profile">
                                    <div className="people-card">
                                        {item?.schedule?.client?.avatar_url ? (
                                            <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorFromName(
                                                        item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                    ),
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: 20,
                                                }}>
                                                {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                            </Avatar>
                                        )}
                                        <div className="people-profile-card">
                                            <Typography variant="h8" color="text.primary" className="name">
                                                {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary" className="name">
                                                {item?.schedule?.service?.name}
                                            </Typography>
                                        </div>
                                    </div>
                                    {item?.status === 'scheduled' && (
                                        <Chip
                                            label="Scheduled"
                                            variant="filled"
                                            sx={theme => ({
                                                backgroundColor: theme.palette.grey[700],
                                                color: theme.palette.secondary.contrastText,
                                            })}
                                            size="small"
                                        />
                                    )}
                                    {item?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" size="small" />}
                                    {item?.status === 'ended' && (
                                        <Chip
                                            label="Ended"
                                            variant="filled"
                                            sx={theme => ({
                                                backgroundColor: '#02CAD1',
                                                color: theme.palette.primary.contrastText,
                                            })}
                                            size="small"
                                        />
                                    )}
                                    {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                    {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                    {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                </div>
                                <br />
                                <div className="datetime">
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <DateRange fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                        </Typography>
                                    </div>
                                    <Typography variant="body3" color="text.secondary">
                                        |
                                    </Typography>
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <AccessTime fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" fullWidth onClick={submit}>
                                {submitLabel}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
